import { useCallback, useContext, useRef } from 'react'
import { PencilIcon } from '@heroicons/react/outline'
import { toast } from 'react-toastify'

import { Role } from 'models'
import { date } from 'utils'
import { AuthContext } from 'modules/Auth'
import { useEditClient } from 'modules/Clients/hooks/useEditClient'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import { updateClientStatusDates } from 'modules/Clients/services/update'
import { statuses as statusOptions } from 'modules/Clients/helpers/constants'
import { StatusChange } from '../Client/components/StatusChange'
import { addDays } from 'utils/datetime'

const statusLabelMap = Object.fromEntries(
  statusOptions.map((s) => [s.value, s.label])
)

export function StatusTab() {
  const { client, fetchClient } = useEditClient()
  const { user } = useContext(AuthContext)
  const userRole = user?.relationships?.role.attributes.name as Role
  const canEditDates = [Role.ROOT, Role.MANAGER, Role.FINANCE].includes(
    userRole
  )

  const [showConfirmationDialog] = useConfirmationDialog()
  const statusDateEditRef = useRef(new Date())

  const onStatusDateEditRequest = useCallback(
    (statusId: number, currentDate: Date, index: number) => {
      const statuses = client?.relationships?.statuses as any[]
      // statuses[0] is newest; previous (older) is at index+1, next (newer) is at index-1
      // +1 / -1 dia: o back rejeita datas iguais aos vizinhos (ordem estritamente cronológica)
      const minDate = statuses?.[index + 1]?.attributes?.created_at
        ? addDays(new Date(statuses[index + 1].attributes.created_at), 1)
        : new Date('2000-01-01')
      const maxDate =
        index > 0 && statuses?.[index - 1]?.attributes?.created_at
          ? addDays(new Date(statuses[index - 1].attributes.created_at), -1)
          : new Date()

      statusDateEditRef.current = currentDate
      showConfirmationDialog({
        title: 'Editar data de situação',
        message: 'Alterar a data deste registro de situação do cliente?',
        datePicker: true,
        datePickerAttributes: { minDate, maxDate },
        date: currentDate,
        onDateChange: (val) => {
          const d = val instanceof Date ? val : new Date(val)
          statusDateEditRef.current = d
          return d
        },
        onConfirm: async () => {
          try {
            await updateClientStatusDates(client?.id as number, [
              {
                id: statusId,
                created_at: statusDateEditRef.current.toISOString()
              }
            ])
            await fetchClient()
            toast.success('Data atualizada com sucesso!')
          } catch (err: any) {
            toast.error(
              err.suggestedMessage ?? 'Não foi possível atualizar a data'
            )
          }
        }
      })
    },
    [client, fetchClient, showConfirmationDialog]
  )

  const statuses = client?.relationships?.statuses as any[] | undefined

  return (
    <div className="mt-10 animate-fade-in-down px-4 max-w-sm">
      <div className="space-y-6">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-2">
            Situação atual
          </p>
          <StatusChange />
        </div>

        {statuses && statuses.length > 0 && (
          <div className="border border-gray-200 rounded-md px-4 py-3">
            <p className="text-sm font-medium text-gray-500 mb-2">Histórico</p>
            {statuses.map((s: any, idx: number) => (
              <div
                key={s.id}
                className="flex items-center justify-between py-1 text-sm text-gray-900"
              >
                <span>
                  {statusLabelMap[s.attributes.status] ?? s.attributes.status}
                </span>
                <div className="flex items-center gap-1 text-gray-500">
                  {canEditDates ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-emerald-600 group"
                      onClick={() =>
                        onStatusDateEditRequest(
                          s.id,
                          new Date(s.attributes.created_at),
                          idx
                        )
                      }
                    >
                      {date(s.attributes.created_at)}
                      <PencilIcon className="w-3 h-3 text-gray-400 group-hover:text-emerald-600" />
                    </button>
                  ) : (
                    date(s.attributes.created_at)
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
